import "./AnalystPreview.css";
import { StyledSelect } from "./StyledSelect.tsx";
import { useState } from "react";
import { useLoad, usePortal, useRouteReady } from "../api/hooks.ts";
import { ErrorNote } from "./common.tsx";

export function refreshPreview() {
  // Other tabs must discard cached admin/analyst screens when the shared cookie changes.
  try { localStorage.setItem("swt-preview-changed", String(Date.now())); } catch { /* Cookie remains authoritative when storage is unavailable. */ }
  window.location.assign("/");
}

export function AnalystPreviewStart() {
  const { preview } = usePortal();
  return preview ? <p>Exit analyst preview before starting another session.</p> : <PreviewStartForm />;
}

function PreviewStartForm() {
  const { api } = usePortal();
  const load = useLoad(a => a.previewTargets(), []);
  useRouteReady(load.state === "ready");
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  return <section className="ipd-card adm-card" aria-labelledby="preview-start-h">
    <div className="ipd-card-head"><div><h2 id="preview-start-h">Choose a confirmed analyst</h2><p>Read-only · 15 minutes · Your admin identity stays in the audit log</p></div></div>
    <div className="ipd-card-body">
      <p>See a confirmed analyst’s assigned work with analyst permissions. This does not sign in to their Microsoft account or mailbox, or reproduce any additional manager or admin roles they hold.</p>
      {load.state === "loading" ? <p role="status">Loading analysts…</p> : load.state === "error" ? <ErrorNote error={load.error} onRetry={load.reload}/> : !load.data.available ? <p>Analyst preview is not configured on this server.</p> : load.data.analysts.length === 0 ? <p>No confirmed analysts are available to preview. Confirm a mapping first.</p> : <form onSubmit={async e => {
        e.preventDefault(); if (busy || load.state !== "ready" || !load.data.analysts.some(a => a.id === target) || (reason.trim().length < 3 || reason.trim().length > 500)) return; setBusy(true); setError(null);
        try { await api.startPreview(target, reason.trim()); refreshPreview(); } catch(e) { setError(e instanceof Error ? e : new Error("Preview could not start")); setBusy(false); }
      }}>
        <label className="ipd-pick">Analyst<StyledSelect aria-label="Analyst" required value={target} onChange={e => setTarget(e.target.value)}><option value="">Choose an analyst</option>{load.data.analysts.map(a => <option key={a.id} value={a.id}>{a.name} · {a.upn}</option>)}</StyledSelect></label>
        <label className="ipd-pick">Reason for viewing<input className="ipd-input" aria-describedby="preview-reason-hint" required minLength={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)}/></label>
        <p id="preview-reason-hint">3–500 characters. Included with your admin identity in the session’s audit record.</p>
        <button className="ipd-btn ipd-btn--go" disabled={busy || !target || (reason.trim().length < 3 || reason.trim().length > 500)}>{busy ? "Opening…" : "View analyst workspace"}</button>
      </form>}
      {error ? <ErrorNote error={error}/> : null}
    </div>
  </section>;
}

export function AnalystPreviewExit({ onExit }: { onExit: () => Promise<void> }) {
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  return <><button className="ipd-btn" disabled={busy} onClick={async () => { setBusy(true); setError(false); try { await onExit(); refreshPreview(); } catch { setError(true); setBusy(false); } }}>{busy ? "Exiting…" : "Exit analyst preview"}</button>{error ? <p role="alert">Could not exit preview. Try again; no changes have been made.</p> : null}</>;
}
